import React, { Dispatch, SetStateAction, useMemo } from 'react'
import { useAtomValue } from 'jotai'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@node-space/hooks'
import { Icon } from '@node-space/ui-components'
import { PathNames } from 'constants/General'
import { useAccountsContext } from 'hooks/context/useAccountsContext'
import { useThemeContext } from 'hooks/context/useThemeContext'
import { useMappedRoles } from 'hooks/useMappedRoles'
import { usePendingApprovalDetails } from 'hooks/usePendingApprovalDetails'
import { contactCaseCountAtom } from 'pages/Contacts/atoms/contactAtoms'
import { isProduction } from 'utils/environment'

interface UseSidebarLinksProps {
  setSidebarOpen: Dispatch<SetStateAction<boolean>>
}

export const useSidebarLinks = ({ setSidebarOpen }: UseSidebarLinksProps) => {
  const { darkMode } = useThemeContext()

  const contactCaseCount = useAtomValue(contactCaseCountAtom)
  const { accountCapabilities, isVerifiedBusinessAccount, isDeveloperAccount, currentAccount } =
    useAccountsContext()

  const { t } = useTranslation()
  const { enableContactBankAccounts, enableUsLaunchRestrictions, enableRewards } = useFeatureFlags()
  const permissions = useMappedRoles()
  const canDisable = !isProduction && isDeveloperAccount ? false : !isVerifiedBusinessAccount

  const currentAccountReference = currentAccount?.reference || ''
  const { numberOfPendingApprovals } = usePendingApprovalDetails(
    currentAccountReference,
    !!currentAccountReference
  )

  const links = useMemo(() => {
    return [
      ...(!isDeveloperAccount
        ? [
            {
              to: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
              title: t('wallets.title'),
              icon: <Icon name="Wallet" />,
              requiresVerification: canDisable,
              sideBarToggleCallback: setSidebarOpen,
              isHidden: !permissions.wallets.canView,
              testId: 'sidebar-wallets',
            },
          ]
        : [
            {
              to: '/',
              title: t('home'),
              icon: <Icon name="House" />,
              requiresVerification: false,
              sideBarToggleCallback: setSidebarOpen,
              testId: 'sidebar-home',
            },
          ]),
      ...(isDeveloperAccount
        ? [
            {
              to: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
              title: t('wallets.title'),
              icon: <Icon name="Wallet" />,
              requiresVerification: canDisable,
              sideBarToggleCallback: setSidebarOpen,
              isHidden: !permissions.wallets.canView,
              testId: 'sidebar-wallets',
            },
          ]
        : [
            {
              to: PathNames.TRANSACTION_HISTORY,
              title: t('wallets.transactionHistory'),
              icon: <Icon name="ListMagnifyingGlass" />,
              requiresVerification: canDisable,
              sideBarToggleCallback: setSidebarOpen,
              isHidden: !permissions.wallets.canView,
              testId: 'sidebar-transaction-history',
            },
          ]),
      ...(isDeveloperAccount
        ? [
            {
              to: PathNames.TRANSACTION_HISTORY,
              title: t('wallets.transactionHistory'),
              icon: <Icon name="ListMagnifyingGlass" />,
              requiresVerification: canDisable,
              sideBarToggleCallback: setSidebarOpen,
              isHidden: !permissions.wallets.canView,
              testId: 'sidebar-transaction-history',
            },
          ]
        : []),
      {
        title: t('sidebar.cryptoPayments'),
        to: `${PathNames.PAYMENTS}${PathNames.CRYPTO}`,
        testId: 'sidebar-crypto-payments',
        isHidden: !permissions.payments.canView,
        icon: <Icon name="CreditCard" />,
        featureName: 'payments',
        sideBarToggleCallback: setSidebarOpen,
        requiresVerification: canDisable,
      },
      ...(!enableUsLaunchRestrictions
        ? [
            {
              title: t('sidebar.channelPayments'),
              to: `${PathNames.PAYMENTS}${PathNames.CHANNEL}`,
              testId: 'sidebar-channel-payments',
              icon: <Icon name="QrCode" />,
              isHidden: !permissions.payments.canView,
              featureName: 'payments',
              sideBarToggleCallback: setSidebarOpen,
              requiresVerification: canDisable,
            },
          ]
        : []),
      {
        to: PathNames.MISSING_CONTACTS,
        icon: <Icon name="UserCirclePlus" />,
        requiresVerification: false,
        sideBarToggleCallback: setSidebarOpen,
        title: t('contacts.missingContacts'),
        testId: 'sidebar-missing-contacts',
        highlight: contactCaseCount,
      },
      {
        to: PathNames.MASS_PAYOUTS,
        icon: <Icon name="BoxArrowUp" />,
        requiresVerification: false,
        sideBarToggleCallback: setSidebarOpen,
        title: t('massPayouts.massPayouts'),
        testId: 'sidebar-mass-payouts',
      },
      {
        to: PathNames.CUSTOMERS,
        icon: <Icon name="Storefront" />,
        requiresVerification: false,
        sideBarToggleCallback: setSidebarOpen,
        title: t('customers.sidebarTitle'),
        testId: 'sidebar-customers',
      },
      {
        to: `${PathNames.APPROVALS}`,
        title: t('approvals.title'),
        icon: <Icon name="Timer" />,
        requiresVerification: false,
        sideBarToggleCallback: setSidebarOpen,
        testId: 'sidebar-approvals',
        highlight: numberOfPendingApprovals,
      },
      ...(enableRewards
        ? [
            {
              to: PathNames.REWARDS,
              title: t('rewards.title'),
              icon: <Icon name="SketchLogo" />,
              requiresVerification: false,
              isHidden: !accountCapabilities?.isEarningRewardsCapabilityEnabled,
              sideBarToggleCallback: setSidebarOpen,
              testId: 'sidebar-rewards',
            },
          ]
        : []),
      {
        to: `${PathNames.CRYPTO_CONTACTS}`,
        title: t(enableContactBankAccounts ? 'contacts.titleV2' : 'contacts.title'),
        icon: <Icon name="AddressBook" />,
        requiresVerification: false,
        sideBarToggleCallback: setSidebarOpen,
        isHidden: !permissions.contacts.canView,
        testId: 'sidebar-crypto-contacts',
      },
      {
        to: `${PathNames.BANK_BENEFICIARIES}`,
        title: t('bankBeneficiaries.title'),
        icon: <Icon name="Bank" />,
        requiresVerification: false,
        sideBarToggleCallback: setSidebarOpen,
        testId: 'sidebar-bank-beneficiaries',
      },

      {
        to: PathNames.INTEGRATIONS,
        title: t('integrations.title'),
        icon: <Icon name="Code" />,
        requiresVerification: false,
        sideBarToggleCallback: setSidebarOpen,
        testId: 'sidebar-integrations',
      },
    ]
  }, [
    isDeveloperAccount,
    permissions,
    canDisable,
    enableUsLaunchRestrictions,
    enableRewards,
    numberOfPendingApprovals,
    contactCaseCount,
    accountCapabilities,
  ])

  const footerLink = useMemo(() => {
    return {
      title: 'BVNK',
      logoUrl: darkMode
        ? 'https://assets.bvnk.com/logo_bvnk_light.svg'
        : 'https://assets.bvnk.com/logo_bvnk_dark.svg',
      to: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
      url: `${PathNames.WALLETS}${PathNames.WALLET_DASHBOARD}`,
    }
  }, [darkMode])

  return {
    links,
    footerLink,
  }
}
